import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { StorageService } from 'app/services/storage.service';
import { KeysStorage } from 'app/comum/keys-storage';
import { NotificacaoService } from 'app/services/notificacao.service';
import { AssinaturaService } from 'app/services/assinatura.service';
import { SweetAlertService } from 'app/services/sweetalert.service';
import {
    AssinaturaResultado,
    AssinaturaStatus,
    DescricaoFormaPagamento,
    DescricaoStatusAssinatura,
    FormaPagamentoAssinatura,
    StatusAssinatura,
} from 'app/models/assinatura.model';

type Tela = 'loading' | 'assinar' | 'pagamento' | 'gerenciar';

@Component({
    selector: 'app-assinatura',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatButtonModule, MatRadioModule, FormsModule, RouterModule],
    templateUrl: './assinatura.component.html',
    styleUrls: ['./assinatura.component.scss']
})
export class AssinaturaComponent implements OnInit {
    private storageService = inject(StorageService);
    private notificacaoService = inject(NotificacaoService);
    private assinaturaService = inject(AssinaturaService);
    private sweetAlertService = inject(SweetAlertService);

    tela: Tela = 'loading';
    loading = false;

    // Trial
    trialExpirado = false;
    dataFimTrial: Date | null = null;
    diasRestantes = 0;

    // Status da assinatura
    assinaturaStatus: AssinaturaStatus | null = null;
    statusInfo = { texto: '', cor: '', icone: '' };
    formaPagamentoInfo = { texto: '', icone: '' };

    // Formulário de assinatura
    formaPagamentoSelecionada = FormaPagamentoAssinatura.PIX;
    formasPagamento = DescricaoFormaPagamento.todos();

    // Resultado do pagamento
    resultadoPagamento: AssinaturaResultado | null = null;

    readonly features = [
        { icon: 'event_note', label: 'Agenda eletrônica completa' },
        { icon: 'description', label: 'Prontuário digital' },
        { icon: 'attach_money', label: 'Controle financeiro' },
        { icon: 'notifications_active', label: 'Lembretes por WhatsApp' },
        { icon: 'bar_chart', label: 'Dashboard com indicadores' },
        { icon: 'people', label: 'Gestão de clientes ilimitada' },
        { icon: 'folder', label: 'Documentos e formulários' },
        { icon: 'calendar_month', label: 'Calendário integrado' },
    ];

    async ngOnInit() {
        this.calcularTrial();
        await this.carregarStatus();
    }

    private calcularTrial() {
        const statusAssinatura = this.storageService.get(KeysStorage.statusAssinatura());
        if (statusAssinatura === '2') {
            this.trialExpirado = false;
            this.diasRestantes = 0;
            return;
        }

        const dataFimStr = this.storageService.get(KeysStorage.dataFimTrial());
        if (dataFimStr) {
            this.dataFimTrial = new Date(dataFimStr);
            const agora = new Date();
            this.diasRestantes = Math.max(0, Math.ceil((this.dataFimTrial.getTime() - agora.getTime()) / (1000 * 60 * 60 * 24)));
            this.trialExpirado = this.diasRestantes <= 0;
        }
    }

    async carregarStatus() {
        this.tela = 'loading';
        try {
            this.assinaturaStatus = await this.assinaturaService.obterStatus();
            this.statusInfo = DescricaoStatusAssinatura.obter(this.assinaturaStatus.status);
            this.formaPagamentoInfo = DescricaoFormaPagamento.obter(this.assinaturaStatus.formaPagamento);

            // Sincronizar status da assinatura no localStorage
            this.storageService.set(String(this.assinaturaStatus.status), KeysStorage.statusAssinatura());
            if (this.assinaturaStatus.ativa) {
                this.diasRestantes = 0;
            }

            this.definirTela();
        } catch (erro: any) {
            this.notificacaoService.mensagemErro('Erro ao carregar status da assinatura.');
            this.tela = 'assinar';
        }
    }

    private definirTela() {
        if (!this.assinaturaStatus) {
            this.tela = 'assinar';
            return;
        }
        if (this.assinaturaStatus.status === StatusAssinatura.Ativa) {
            this.tela = 'gerenciar';
        } else if (this.assinaturaStatus.status === StatusAssinatura.Pendente && this.assinaturaStatus.formaPagamento) {
            this.tela = 'gerenciar';
        } else if (this.assinaturaStatus.dataProximoVencimento) {
            // Usuário tem assinatura (mesmo cancelada/inadimplente) → mostrar tela de gerenciamento com opção de reativar
            this.tela = 'gerenciar';
        } else {
            this.tela = 'assinar';
        }
    }

    async assinar() {
        this.loading = true;
        try {
            this.resultadoPagamento = await this.assinaturaService.assinar({
                formaPagamento: this.formaPagamentoSelecionada,
            });
            this.tela = 'pagamento';
            this.notificacaoService.mensagemSucesso('Assinatura criada! Efetue o pagamento para ativar.');
        } catch (erro: any) {
            const msg = erro?.error?.mensagem || erro?.error || 'Erro ao criar assinatura.';
            this.notificacaoService.mensagemErro(msg);
        } finally {
            this.loading = false;
        }
    }

    async obterLinkPagamento() {
        this.loading = true;
        try {
            this.resultadoPagamento = await this.assinaturaService.obterLinkPagamento();
            this.tela = 'pagamento';
        } catch (erro: any) {
            const msg = erro?.error?.mensagem || erro?.error || 'Nenhuma cobrança pendente encontrada.';
            this.notificacaoService.mensagemAlerta(msg);
        } finally {
            this.loading = false;
        }
    }

    async cancelar() {
        const resultado = await this.sweetAlertService.alertaConfirmacao(
            'Cancelar assinatura?',
            'Você perderá o acesso ao sistema após o término do período pago.',
            'Sim, cancelar',
            'Não, voltar'
        );

        if (!resultado.isConfirmed) return;

        this.loading = true;
        try {
            await this.assinaturaService.cancelar();
            this.notificacaoService.mensagemSucesso('Assinatura cancelada com sucesso.');
            await this.carregarStatus();
        } catch (erro: any) {
            const msg = erro?.error?.mensagem || erro?.error || 'Erro ao cancelar assinatura.';
            this.notificacaoService.mensagemErro(msg);
        } finally {
            this.loading = false;
        }
    }

    copiarQrCode() {
        if (this.resultadoPagamento?.qrCodePix) {
            navigator.clipboard.writeText(this.resultadoPagamento.qrCodePix);
            this.notificacaoService.mensagemSucesso('Código PIX copiado!');
        }
    }

    abrirUrlPagamento() {
        if (this.resultadoPagamento?.urlPagamento) {
            window.open(this.resultadoPagamento.urlPagamento, '_blank');
        }
    }

    voltarParaGerenciar() {
        this.resultadoPagamento = null;
        this.carregarStatus();
    }

    formatarData(dataStr: string | null): string {
        if (!dataStr) return '—';
        const data = new Date(dataStr);
        return data.toLocaleDateString('pt-BR');
    }

    get diasAteVencimento(): number {
        if (!this.assinaturaStatus?.dataProximoVencimento) return 0;
        const vencimento = new Date(this.assinaturaStatus.dataProximoVencimento);
        const agora = new Date();
        return Math.ceil((vencimento.getTime() - agora.getTime()) / (1000 * 60 * 60 * 24));
    }
}
